import type { GovernedProjectMemoryList } from "@esi/shared";
import {
  type AssignableProjectRole,
  type ClassificationLevel,
  CSRF_HEADER,
  classificationSchemeSchema,
  type GovernedProjectDetail,
  type UpdateProjectRequest,
} from "@esi/shared";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff, Lock, TriangleAlert } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatBytes, formatNumber, formatUsd } from "@/components/charts";
import { Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { formatDate, formatDateTime } from "../i18n/format.ts";
import { rejectPending } from "../projects/access-requests.ts";
import { usePrincipals } from "../projects/queries.ts";
import type { AccessApi } from "../projects/settings/access-api.ts";
import { BehaviourSection, type SettingsBackend } from "../projects/settings/BehaviourSection.tsx";
import { GeneralSection } from "../projects/settings/GeneralSection.tsx";
import { AccessList, type ListState } from "../projects/settings/MembersSection.tsx";
import { SettingsSection } from "../projects/settings/SettingsSection.tsx";
import { useSession } from "../session.tsx";
import { myAccessRequestsKey, PendingApprovalNotice } from "./AccessRequests.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { LevelsDisclosure } from "./classification/ReadOnlyLevels.tsx";
import { useScopeEntities } from "./reports/queries.ts";

/**
 * One governed entity project in the backoffice (spec `projects` "Project governance by
 * admins"): its metadata, who holds which role and how it is configured. Governance never opens
 * its content: a change that would give the admin a role, or widen who reads the project, waits
 * for the project's approval ("Pending owner approval"). Read-only under a support grant.
 */
export function GovernedProjectView({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { admin, readOnly } = useAdminApi();
  const detail = useQuery({
    queryKey: governedKey(projectId),
    queryFn: async (): Promise<GovernedProjectDetail> =>
      ok(await admin.projects[":projectId"].$get({ param: { projectId } })),
    retry: false,
  });
  const entities = useScopeEntities();
  if (detail.isPending) {
    return (
      <div data-esi-content="projects" className="flex flex-col gap-6" aria-busy="true">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (detail.isError) {
    const personal = detail.error instanceof ApiError && detail.error.code === "personal_project";
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={personal ? t("governance.personal") : t("governance.loadFailed")} />
        <Notice tone={personal ? "neutral" : "danger"}>
          {personal ? t("governance.personalDetail") : detail.error.message}
        </Notice>
      </div>
    );
  }
  const { owner } = detail.data.project;
  const ownerName =
    owner.kind === "entity"
      ? (entities.data?.find((e) => e.id === owner.entityId)?.name ?? null)
      : null;
  return (
    <GovernedProjectBody
      detail={detail.data}
      ownerName={ownerName}
      editable={!readOnly && detail.data.project.frozenAt === null}
    />
  );
}

function GovernedProjectBody({
  detail,
  ownerName,
  editable,
}: {
  detail: GovernedProjectDetail;
  ownerName: string | null;
  editable: boolean;
}) {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const { project } = detail;
  const projectId = project.id;
  const save = useGovernedWrite((headers, json: UpdateProjectRequest) =>
    admin.projects[":projectId"].$patch({ param: { projectId }, json }, headers),
  );
  const settings = useSettingsBackend(projectId, detail);
  const access = useGovernedAccess(projectId);
  const principals = usePrincipals(editable);
  const fetching = useIsFetching({ queryKey: governedKey(projectId) }) > 0;
  return (
    <div data-esi-content="projects" className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <PageHeader
          title={project.name}
          lead={ownerName ?? t("governance.governed.entityUnknown")}
        />
        <p className="flex max-w-prose items-start gap-1.5 text-xs text-fg-muted">
          <Lock aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.75} />
          {t("governance.metadataOnly")}
        </p>
        {project.frozenAt ? (
          <Notice tone="neutral">{t("settings.members.refused.frozen")}</Notice>
        ) : null}
        <PendingApprovalNotice projectId={projectId} projectName={project.name} />
      </div>
      <Overview detail={detail} ownerName={ownerName} />
      <div className="flex min-w-0 flex-col divide-y divide-line border-t border-line pt-8">
        <SettingsSection id="general">
          <GeneralSection
            key={`${project.name}|${project.description}|${project.visibility}`}
            project={{ ...project, ownerName }}
            editable={editable}
            save={save}
          />
        </SettingsSection>
        <AccessList
          project={project}
          members={loaded(detail.members, fetching)}
          groups={loaded(detail.groups, fetching)}
          rules={loaded(detail.rules, fetching)}
          principals={principals}
          manageMembers={editable}
          manageGrants={editable}
          // Governance reassigns the owner (its own route), it never transfers as the owner.
          mayTransfer={false}
          ownerOnlyNote={false}
          access={access}
          refresh={() => undefined}
        />
        <SettingsSection id="behaviour">
          <BehaviourSection
            project={{ id: projectId, owner: project.owner, ownerName }}
            editable={editable}
            backend={settings}
          />
        </SettingsSection>
        <GovernedMemories projectId={projectId} />
      </div>
    </div>
  );
}

/**
 * The project's agreed memories (spec `projects` "Governance without content access",
 * memory-governance): text only where the admin can read the sources, otherwise a hidden row;
 * never pending suggestions. Read-only: the project's owners and managers curate them.
 */
function GovernedMemories({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const memories = useQuery({
    // Its own key, not under the project's: each read is audited, so it is fetched once per
    // visit, not on every project refresh or window focus.
    queryKey: ["admin", "governed-memories", projectId],
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<GovernedProjectMemoryList> =>
      ok(await admin.projects[":projectId"].memories.$get({ param: { projectId } })),
    retry: false,
  });
  return (
    <section
      id="memories"
      aria-labelledby="memories-heading"
      className="flex scroll-mt-6 flex-col gap-5 py-8"
    >
      <div className="flex flex-col gap-1">
        <h2 id="memories-heading" className="text-base font-semibold text-fg">
          {t("governance.memories.title")}
        </h2>
        <p className="max-w-prose text-sm leading-relaxed text-fg-secondary">
          {t("governance.memories.lead")}
        </p>
      </div>
      {memories.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : memories.isError ? (
        <Notice tone="danger">{memories.error.message}</Notice>
      ) : memories.data.items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-fg-muted">
          {t("governance.memories.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {memories.data.items.map((m) => (
            <li key={m.id} className="flex flex-col gap-1 px-4 py-3">
              {m.hidden ? (
                <p className="flex items-center gap-1.5 text-sm text-fg-muted">
                  <EyeOff aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
                  {t("governance.memories.hidden")}
                </p>
              ) : (
                <p className="text-sm text-fg">{m.text}</p>
              )}
              <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
                {m.sourceCount > 0 ? (
                  <span className="tabular-nums">
                    {t("governance.memories.sources", { count: m.sourceCount })}
                  </span>
                ) : null}
                {m.trust === "external" ? (
                  <>
                    {m.sourceCount > 0 ? <span aria-hidden="true">·</span> : null}
                    <span className="inline-flex items-center gap-1 text-warning-fg">
                      <TriangleAlert aria-hidden="true" className="size-3" strokeWidth={1.75} />
                      {t("governance.memories.unverified")}
                    </span>
                  </>
                ) : null}
                {m.sourceCount > 0 || m.trust === "external" ? (
                  <span aria-hidden="true">·</span>
                ) : null}
                <span className="tabular-nums">
                  {m.lastUsedAt
                    ? t("governance.memories.lastUsed", { date: formatDate(m.lastUsedAt) })
                    : t("governance.memories.neverUsed")}
                </span>
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const governedKey = (projectId: string) => ["admin", "projects", projectId] as const;

/** Counts, size, classifications, activity and spend: a definition list, no content. */
function Overview({
  detail,
  ownerName,
}: {
  detail: GovernedProjectDetail;
  ownerName: string | null;
}) {
  const { t } = useTranslation();
  const { project } = detail;
  const files = Object.values(project.documents).reduce((n, c) => n + (c ?? 0), 0);
  // Parsed, as the backoffice row does: without a scheme the line has no levels to explain.
  const parsed = classificationSchemeSchema.safeParse(
    detail.settings.find((s) => s.key === "knowledge.classificationScheme")?.value,
  );
  const scheme: ClassificationLevel[] = parsed.success ? parsed.data : [];
  const used = Object.entries(project.classifications).filter(([, n]) => n > 0);
  const classifications = used.map(
    ([id, n]) => `${scheme.find((l) => l.id === id)?.label ?? id} ${formatNumber(n)}`,
  );
  const rows: [string, ReactNode][] = [
    [t("governance.ownerEntity"), ownerName ?? t("governance.governed.entityUnknown")],
    [t("governance.visibility"), t(`project.visibility.${project.visibility}`)],
    [
      t("governance.files"),
      t("governance.filesValue", { count: files, size: formatBytes(project.storageBytes) }),
    ],
    [
      t("governance.classifications"),
      classifications.length > 0 ? (
        <ProjectLevels
          key="levels"
          line={classifications.join(" · ")}
          scheme={scheme}
          settings={detail.settings}
          used={used.map(([id]) => id)}
        />
      ) : (
        t("governance.noClassifications")
      ),
    ],
    [
      t("governance.members"),
      t("governance.rolesValue", {
        // `count` picks the plural form, `n` is the number as the locale writes it.
        members: t("governance.rolesMembers", {
          count: project.members,
          n: formatNumber(project.members),
        }),
        groups: t("governance.rolesGroups", {
          count: project.groupGrants,
          n: formatNumber(project.groupGrants),
        }),
        rules: t("governance.rolesRules", {
          count: project.ruleGrants,
          n: formatNumber(project.ruleGrants),
        }),
      }),
    ],
    [
      t("governance.lastActivity"),
      project.lastActivityAt ? formatDateTime(project.lastActivityAt) : "—",
    ],
    [t("governance.spend"), project.costUsd30d === null ? "—" : formatUsd(project.costUsd30d)],
    [t("governance.created"), formatDate(project.createdAt)],
    ...(detail.tasks
      ? [
          [
            t("governance.tasks"),
            t("governance.tasksValue", {
              total: formatNumber(detail.tasks.total),
              open: formatNumber(detail.tasks.open),
              overdue: formatNumber(detail.tasks.overdue),
            }),
          ] as [string, ReactNode],
        ]
      : []),
  ];
  return (
    <section aria-label={t("governance.facts")}>
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[12rem_minmax(0,1fr)]">
        {rows.map(([term, value]) => (
          <div key={term} className="flex flex-col gap-0.5 sm:contents">
            <dt className="text-fg-muted">{term}</dt>
            <dd className="text-fg tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The Classifications line and "What these levels mean": the levels the project's files use,
 * read-only, with the editor's wording (openspec change classification-editor, D9).
 */
function ProjectLevels(props: {
  line: string;
  scheme: readonly ClassificationLevel[];
  settings: GovernedProjectDetail["settings"];
  used: readonly string[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col items-start gap-1">
      <span>{props.line}</span>
      {props.scheme.length > 0 ? (
        <LevelsDisclosure
          scheme={props.scheme}
          settings={props.settings}
          only={props.used}
          label={t("adminSettings.levels.projectLevels")}
          open={open}
          onToggle={() => setOpen(!open)}
        />
      ) : null}
    </div>
  );
}

type WriteHeaders = { headers: Record<string, string> };

/**
 * A governance write with the session's CSRF header: a refusal becomes an `ApiError`, and the
 * governed project and the list refresh once it settles, whatever the outcome.
 */
function useGovernedWrite<A extends unknown[]>(
  send: (
    headers: WriteHeaders,
    ...args: A
  ) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>,
): (...args: A) => Promise<unknown> {
  const queryClient = useQueryClient();
  const { csrfToken } = useSession();
  return async (...args: A) => {
    try {
      const res = await send({ headers: { [CSRF_HEADER]: csrfToken } }, ...args);
      if (!res.ok) {
        throw await apiError(res);
      }
      // Waiting for the project's approval: nothing applied, the pending notice explains it.
      rejectPending(res);
      return res.status === 204 ? null : await res.json();
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "projects"] }),
        queryClient.invalidateQueries({ queryKey: myAccessRequestsKey }),
      ]);
    }
  };
}

function useSettingsBackend(projectId: string, detail: GovernedProjectDetail): SettingsBackend {
  const { admin } = useAdminApi();
  const put = useGovernedWrite((headers, key: string, value: unknown) =>
    admin.projects[":projectId"].settings[":key"].$put(
      { param: { projectId, key }, json: { value } },
      headers,
    ),
  );
  const reset = useGovernedWrite((headers, key: string) =>
    admin.projects[":projectId"].settings[":key"].$delete({ param: { projectId, key } }, headers),
  );
  return {
    settings: detail.settings,
    error: null,
    put,
    reset: async (key) => {
      await reset(key);
    },
    // Each write already refreshes the governed project once it settles.
    refresh: async () => undefined,
  };
}

/** The governance routes per kind, for the shared access list. */
function useGovernedAccess(projectId: string): AccessApi {
  const { admin } = useAdminApi();
  const p = admin.projects[":projectId"];
  const putRule = useGovernedWrite((headers, ruleId: string, role: AssignableProjectRole) =>
    p.rules[":ruleId"].$put({ param: { projectId, ruleId }, json: { role } }, headers),
  );
  return {
    add: {
      person: useGovernedWrite((headers, email: string, role: AssignableProjectRole) =>
        p.members.$post({ param: { projectId }, json: { email, role } }, headers),
      ),
      group: useGovernedWrite((headers, groupId: string, role: AssignableProjectRole) =>
        p.groups.$post({ param: { projectId }, json: { groupId, role } }, headers),
      ),
      rule: putRule,
    },
    setRole: {
      person: useGovernedWrite((headers, userId: string, role: AssignableProjectRole) =>
        p.members[":userId"].$put({ param: { projectId, userId }, json: { role } }, headers),
      ),
      group: useGovernedWrite((headers, groupId: string, role: AssignableProjectRole) =>
        p.groups[":groupId"].$put({ param: { projectId, groupId }, json: { role } }, headers),
      ),
      rule: putRule,
    },
    remove: {
      person: useGovernedWrite((headers, userId: string) =>
        p.members[":userId"].$delete({ param: { projectId, userId } }, headers),
      ),
      group: useGovernedWrite((headers, groupId: string) =>
        p.groups[":groupId"].$delete({ param: { projectId, groupId } }, headers),
      ),
      rule: useGovernedWrite((headers, ruleId: string) =>
        p.rules[":ruleId"].$delete({ param: { projectId, ruleId } }, headers),
      ),
    },
  };
}

/** A list the detail already holds, in the shape the access list reads. */
const loaded = <T,>(data: T[], isFetching: boolean): ListState<T> => ({
  data,
  error: null,
  isSuccess: true,
  isError: false,
  isFetching,
});
